import { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Tabs, 
  Tab,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Inventory as InventoryIcon,
  Assignment as AssignIcon,
  AssignmentReturn as ReturnIcon,
  QrCodeScanner as StatusIcon,
  CompareArrows as TransferIcon,
  Scanner as StackScanIcon,
  QrCode as TemplateIcon,
  Warehouse as WarehouseIcon,
  Layers as BayIcon,
} from '@mui/icons-material';
import FeatureGate from './FeatureGate';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';

/**
 * Persistent Navigation Component for Inventory Pages
 * Provides consistent tabbed navigation across all inventory-related pages
 */
export default function InventoryNavigation({ children, currentPage = 'summary' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isFeatureEnabled } = useFeatureFlags();

  // Define inventory navigation tabs (all tabs including feature-gated ones)
  const allInventoryTabs = [
    { 
      key: 'summary', 
      label: 'Summary', 
      icon: <InventoryIcon />
    },
    { 
      key: 'status', 
      label: 'Status & Actions', 
      icon: <StatusIcon />
    },
    { 
      key: 'stacks', 
      label: 'Job Stacks', 
      icon: <StackScanIcon />,
      featureFlag: 'QUICK_STACK_SCANNING'
    },
    { 
      key: 'templates', 
      label: 'Equipment Templates', 
      icon: <TemplateIcon />
    },
    { 
      key: 'warehouses', 
      label: 'Warehouse Management', 
      icon: <WarehouseIcon />
    }
  ];

  // Filter tabs based on feature flags (only include enabled tabs)
  const inventoryTabs = allInventoryTabs.filter(tab => 
    !tab.featureFlag || (tab.featureFlag && isFeatureEnabled && isFeatureEnabled(tab.featureFlag))
  );

  // Determine current tab based on currentPage prop or URL
  const getCurrentTabIndex = () => {
    // First try to match by currentPage prop in the filtered tabs
    const pageIndex = inventoryTabs.findIndex(tab => tab.key === currentPage);
    if (pageIndex !== -1) return pageIndex;

    // Fallback to URL query parameter
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      const tabIndex = inventoryTabs.findIndex(t => t.key === tab);
      if (tabIndex !== -1) return tabIndex;
    }
    
    return 0; // Default to summary tab
  };

  const [currentTab, setCurrentTab] = useState(getCurrentTabIndex());

  // Update tab when currentPage prop or location changes
  useEffect(() => {
    setCurrentTab(getCurrentTabIndex());
  }, [currentPage, location.search]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    const selectedTab = inventoryTabs[newValue];
    if (selectedTab) {
      setCurrentTab(newValue);
      
      // Always stay on the inventory page and use tab query parameter
      navigate(`/inventory?tab=${selectedTab.key}`, { replace: false });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange} 
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          centered={!isMobile}
          aria-label="inventory navigation tabs"
          sx={{
            bgcolor: '#FFFFFF',
            '& .MuiTab-root': {
              color: '#666666',
              fontWeight: 500,
              minWidth: isMobile ? 'auto' : 120,
              '&.Mui-selected': {
                color: '#2C2C2C',
                fontWeight: 600,
              },
              '&:hover': {
                color: '#4A4A4A',
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '2px 2px 0 0',
              backgroundColor: '#2C2C2C',
            }
          }}
        >
          {inventoryTabs.map((tab, index) => (
            <Tab 
              key={tab.key}
              icon={tab.icon} 
              label={tab.label}
              aria-label={`Navigate to ${tab.label}`}
            />
          ))}
        </Tabs>
        
        <Divider />
        
        {/* Page Content */}
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      </Paper>
    </Box>
  );
}